// L'EFFACEUR - Push Notifications Hook
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'serviceWorker' in navigator && 
                        'PushManager' in window && 
                        'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        // Fetch VAPID public key
        fetchVapidKey();
        // Check current subscription status
        checkSubscription();
      }
    };
    
    checkSupport();
  }, []);

  const fetchVapidKey = async () => {
    try {
      const res = await axios.get(`${API}/notifications/vapid-public-key`);
      setVapidPublicKey(res.data.publicKey);
    } catch (error) {
      console.error('Failed to fetch VAPID key:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  };

  // Convert VAPID key from base64url to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    // CCC.15 : message plus clair si VAPID pas configuré (pas "non supportées" qui est faux)
    if (!isSupported) {
      toast.error('Votre navigateur ne supporte pas les notifications. Utilisez Chrome, Edge ou Firefox récent.');
      return false;
    }
    if (!vapidPublicKey) {
      toast.info('Les notifications sont en cours de configuration. Réessaie dans quelques instants ou contacte le support.');
      return false;
    }

    setIsLoading(true);

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Permission refusée pour les notifications');
        setIsLoading(false);
        return false;
      }

      // Register service worker if not already registered
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/notifications/subscribe`,
        {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
          }
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        }
      );

      setIsSubscribed(true);
      toast.success('🔔 Notifications activées !');
      return true;

    } catch (error) {
      console.error('Subscribe error:', error);
      toast.error('Erreur lors de l\'activation des notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, vapidPublicKey]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove from backend
        const token = localStorage.getItem('token');
        await axios.delete(
          `${API}/notifications/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`,
          {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          }
        );
      }

      setIsSubscribed(false);
      toast.success('Notifications désactivées');
      return true;

    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error('Erreur lors de la désactivation');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Toggle subscription
  const toggle = useCallback(async () => {
    if (isSubscribed) {
      return await unsubscribe();
    } else {
      return await subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  // CCC.14 : isReady = supporté ET clé VAPID chargée. Si false, on cache le bouton
  // dans la sidebar pour éviter le toast "Notifications non supportées" au clic.
  const isReady = isSupported && !!vapidPublicKey;

  return {
    isSupported,
    isReady,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    toggle
  };
};

export default usePushNotifications;
